import { useState } from "react";
import { toast } from "sonner";
import { Link2, Users } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

function LinkProfileDialog({ account, parallelRole, appalti, onClose, onLinked }) {
  const parallelLabel = parallelRole === "UFFICIO" ? "Ufficio" : "Lavoratore";
  const [ruolo, setRuolo] = useState(account?.ruolo_professionale || "");
  const [monteOre, setMonteOre] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const toggle = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const ids = [...selected];
    if (!ruolo.trim()) return setError("Indica il ruolo professionale del profilo collegato");
    if (ids.length === 0) return setError("Associa almeno un appalto attivo");
    const monte = monteOre === "" ? null : Number(monteOre);
    if (parallelRole === "LAVORATORE" && (!monte || monte <= 0)) return setError("Il monte ore settimanale contrattuale è obbligatorio (maggiore di 0)");
    setSaving(true);
    try {
      await api.post(`/admin/accounts/${account.id}/link-profile`, {
        ruolo_professionale: ruolo, appalto_ids: ids, monte_ore_settimanale_contrattuale: monte,
      });
      toast.success(`Profilo ${parallelLabel} collegato allo stesso account`);
      onLinked();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl max-h-[92vh] overflow-y-auto" data-testid="link-profile-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Collega profilo {parallelLabel}</DialogTitle>
          <DialogDescription>
            Stessa identità e credenziali dell'account esistente: nessuna seconda email, password o attivazione. Compila solo i dati specifici del profilo {parallelLabel}.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {error && <div data-testid="link-profile-error" className="rounded-lg bg-destructive/10 text-destructive text-sm px-3 py-2 border border-destructive/20">{error}</div>}
          <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2.5 space-y-0.5" data-testid="link-profile-common">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Dati comuni (ereditati)</p>
            <p className="text-sm text-slate-700">{account.nome} {account.cognome}</p>
            <p className="text-sm text-slate-500">{account.email}</p>
          </div>
          <div className="space-y-1.5">
            <Label>Ruolo professionale ({parallelLabel})</Label>
            <Input value={ruolo} data-testid="link-profile-ruolo-input" onChange={(e) => setRuolo(e.target.value)} placeholder="Es. Impiegato" />
          </div>
          {parallelRole === "LAVORATORE" && (
            <div className="space-y-1.5">
              <Label>Monte ore settimanale contrattuale <span className="text-destructive">*</span></Label>
              <Input type="number" step="0.5" min="0" value={monteOre} data-testid="link-profile-monteore-input" onChange={(e) => setMonteOre(e.target.value)} placeholder="Es. 30" />
            </div>
          )}
          <div className="space-y-2">
            <Label>Appalti associati al profilo {parallelLabel}</Label>
            <div className="rounded-lg border border-slate-200 divide-y" data-testid="link-profile-appalti-list">
              {appalti.length === 0 && <p className="text-sm text-slate-500 p-3">Nessun appalto attivo disponibile.</p>}
              {appalti.map((a) => (
                <label key={a.id} className="flex items-center gap-3 p-3 cursor-pointer hover:bg-slate-50">
                  <Checkbox checked={selected.has(a.id)} onCheckedChange={() => toggle(a.id)} data-testid={`link-profile-appalto-${a.id}`} />
                  <span className="text-sm font-medium text-slate-700">{a.nome}</span>
                </label>
              ))}
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={saving} data-testid="link-profile-cancel-btn" className="rounded-lg h-11">Annulla</Button>
            <Button type="submit" disabled={saving} data-testid="link-profile-save-btn" className="rounded-lg h-11 font-semibold">
              {saving ? "Collegamento…" : `Collega profilo ${parallelLabel}`}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function AccountDialog({ role, appalti, account, onClose, onSaved }) {
  const isEdit = !!account;
  const roleLabel = role === "UFFICIO" ? "Ufficio" : "Lavoratore";
  const [nome, setNome] = useState(account?.nome || "");
  const [cognome, setCognome] = useState(account?.cognome || "");
  const [ruolo, setRuolo] = useState(account?.ruolo_professionale || "");
  const [email, setEmail] = useState(account?.email || "");
  const [monteOre, setMonteOre] = useState(account?.monte_ore_settimanale_contrattuale ?? "");
  const [selected, setSelected] = useState(new Set(account?.appalto_ids || []));
  const [serviceMap, setServiceMap] = useState(() => ({ ...(account?.appalto_services || {}) }));
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [showLink, setShowLink] = useState(false);
  const hasLinked = !!account?.linked_profile;
  const parallelRole = role === "UFFICIO" ? "LAVORATORE" : "UFFICIO";
  const parallelLabel = parallelRole === "UFFICIO" ? "Ufficio" : "Lavoratore";

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) {
      next.delete(id);
      setServiceMap((p) => { const n = { ...p }; delete n[id]; return n; });
    } else {
      next.add(id);
    }
    setSelected(next);
  };

  const isLimited = (aid) => Array.isArray(serviceMap[aid]);
  const setLimited = (aid, on) => setServiceMap((prev) => {
    const next = { ...prev };
    if (on) next[aid] = next[aid] || [];
    else delete next[aid];
    return next;
  });
  const toggleService = (aid, slug) => setServiceMap((prev) => {
    const cur = new Set(prev[aid] || []);
    cur.has(slug) ? cur.delete(slug) : cur.add(slug);
    return { ...prev, [aid]: [...cur] };
  });
  const buildServices = () => {
    const out = {};
    for (const aid of selected) {
      if (Array.isArray(serviceMap[aid]) && serviceMap[aid].length > 0) out[aid] = serviceMap[aid];
    }
    return out;
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const ids = [...selected];
    if (!nome.trim() || !cognome.trim() || !ruolo.trim()) return setError("Compila nome, cognome e ruolo professionale");
    if (!isEdit && !email.trim()) return setError("Inserisci l'email");
    if (ids.length === 0) return setError("Associa almeno un appalto attivo");
    setSaving(true);
    try {
      const monte = monteOre === "" ? null : Number(monteOre);
      if (role === "LAVORATORE" && (!monte || monte <= 0)) {
        setSaving(false);
        return setError("Il monte ore settimanale contrattuale è obbligatorio (maggiore di 0)");
      }
      if (!isEdit) {
        await api.post("/workers", {
          nome, cognome, ruolo_professionale: ruolo, email: email.trim().toLowerCase(),
          profilo_accesso: role, appalto_ids: ids, monte_ore_settimanale_contrattuale: monte,
          ...(role === "UFFICIO" ? { appalto_services: buildServices() } : {}),
        });
        toast.success(`Account ${roleLabel} creato · e-mail di attivazione inviata`);
      } else {
        await api.put(`/workers/${account.id}`, {
          nome, cognome, ruolo_professionale: ruolo, monte_ore_settimanale_contrattuale: monte,
          edit_profile: account.edit_profile || "main",
        });
        if (role === "UFFICIO") {
          await api.patch(`/workers/${account.id}/appalti`, { appalto_ids: ids, appalto_services: buildServices() });
        } else {
          const before = new Set(account.appalto_ids || []);
          const changed = ids.length !== before.size || ids.some((i) => !before.has(i));
          if (changed) await api.patch(`/workers/${account.id}/appalti`, { appalto_ids: ids });
        }
        toast.success("Account aggiornato");
      }
      onSaved();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl max-h-[92vh] overflow-y-auto" data-testid="account-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{isEdit ? "Modifica" : "Nuovo"} account {roleLabel}</DialogTitle>
          <DialogDescription>Associa almeno un appalto attivo. L'e-mail è univoca in tutto il sistema.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {error && <div data-testid="account-error" className="rounded-lg bg-destructive/10 text-destructive text-sm px-3 py-2 border border-destructive/20">{error}</div>}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Nome</Label><Input value={nome} data-testid="account-nome-input" onChange={(e) => setNome(e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Cognome</Label><Input value={cognome} data-testid="account-cognome-input" onChange={(e) => setCognome(e.target.value)} /></div>
          </div>
          <div className="space-y-1.5"><Label>Ruolo professionale</Label><Input value={ruolo} data-testid="account-ruolo-input" onChange={(e) => setRuolo(e.target.value)} placeholder="Es. Educatore" /></div>
          <div className="space-y-1.5">
            <Label>Email</Label>
            <Input type="email" value={email} disabled={isEdit} data-testid="account-email-input" onChange={(e) => setEmail(e.target.value)} placeholder="nome@dominio.it" />
          </div>
          {role === "LAVORATORE" && (
            <div className="space-y-1.5">
              <Label>Monte ore settimanale contrattuale <span className="text-destructive">*</span></Label>
              <Input type="number" step="0.5" min="0" value={monteOre} data-testid="account-monteore-input" onChange={(e) => setMonteOre(e.target.value)} placeholder="Es. 30" />
              {isEdit && account?.monte_ore_mancante && (monteOre === "" || Number(monteOre) <= 0) && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="account-monteore-missing">
                  Dato mancante: inserisci il monte ore settimanale contrattuale per completare l'anagrafica.
                </p>
              )}
            </div>
          )}
          <div className="space-y-2">
            <Label>Appalti associati{role === "UFFICIO" ? " e servizi autorizzati" : ""}</Label>
            <div className="rounded-lg border border-slate-200 divide-y" data-testid="account-appalti-list">
              {appalti.length === 0 && <p className="text-sm text-slate-500 p-3">Nessun appalto attivo disponibile. Crealo nella sezione Appalti.</p>}
              {appalti.map((a) => {
                const on = selected.has(a.id);
                const servizi = (a.servizi || []).filter((s) => s && s.slug);
                const limited = isLimited(a.id);
                return (
                  <div key={a.id}>
                    <label className="flex items-center gap-3 p-3 cursor-pointer hover:bg-slate-50">
                      <Checkbox checked={on} onCheckedChange={() => toggle(a.id)} data-testid={`account-appalto-${a.id}`} />
                      <span className="text-sm font-medium text-slate-700">{a.nome}</span>
                    </label>
                    {on && role === "UFFICIO" && (
                      <div className="pl-10 pr-3 pb-3 space-y-2" data-testid={`account-services-${a.id}`}>
                        {servizi.length === 0 ? (
                          <p className="text-xs text-slate-400">Nessun servizio abilitato su questo appalto.</p>
                        ) : (
                          <>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input type="radio" className="accent-[#3A4A7A]" checked={!limited}
                                onChange={() => setLimited(a.id, false)} data-testid={`account-services-all-${a.id}`} />
                              <span className="text-xs font-medium text-slate-600">Tutti i servizi dell'appalto</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input type="radio" className="accent-[#3A4A7A]" checked={limited}
                                onChange={() => setLimited(a.id, true)} data-testid={`account-services-limit-${a.id}`} />
                              <span className="text-xs font-medium text-slate-600">Limita ai servizi selezionati</span>
                            </label>
                            {limited && (
                              <div className="mt-1 grid grid-cols-1 gap-1.5 rounded-lg bg-slate-50 border border-slate-200 p-2.5">
                                {servizi.map((s) => (
                                  <label key={s.slug} className="flex items-center gap-2.5 cursor-pointer">
                                    <Checkbox checked={(serviceMap[a.id] || []).includes(s.slug)}
                                      onCheckedChange={() => toggleService(a.id, s.slug)}
                                      data-testid={`account-service-${a.id}-${s.slug}`} />
                                    <span className="text-xs text-slate-700">{s.nome}</span>
                                  </label>
                                ))}
                                {(serviceMap[a.id] || []).length === 0 && (
                                  <p className="text-[11px] text-amber-700">Seleziona almeno un servizio, altrimenti l'account vedrà tutti i servizi dell'appalto.</p>
                                )}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="account-save-btn" className="w-full rounded-lg h-11 font-semibold">
              {saving ? "Salvataggio…" : isEdit ? "Salva modifiche" : "Crea account"}
            </Button>
          </DialogFooter>
        </form>

        {isEdit && (
          <div className="mt-1 pt-4 border-t border-slate-200 space-y-3" data-testid="linked-profile-section">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
              <Users className="h-4 w-4 text-slate-500" /> Profili attivi
            </div>
            <div className="flex flex-wrap gap-2" data-testid="profili-attivi">
              {(account.profili_attivi || [roleLabel]).map((p) => (
                <span key={p} className="inline-flex items-center rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-medium px-3 py-1">{p}</span>
              ))}
            </div>
            {hasLinked ? (
              <p className="text-xs text-slate-500" data-testid="linked-profile-info">
                Questo account possiede entrambi i profili collegati alla stessa identità.
              </p>
            ) : (
              <Button type="button" variant="outline" onClick={() => setShowLink(true)}
                data-testid="link-profile-open-btn" className="rounded-lg h-10 font-medium">
                <Link2 className="h-4 w-4 mr-1.5" /> Collega profilo {parallelLabel}
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
    {showLink && (
      <LinkProfileDialog account={account} parallelRole={parallelRole} appalti={appalti}
        onClose={() => setShowLink(false)}
        onLinked={() => { setShowLink(false); onSaved(); }} />
    )}
    </>
  );
}
